import * as storeHelper from "@/shared/storeHelper";
import * as drivewealthService from "@/shared/api/drivewealthService";
import {
    DrivewealthSweep,
    DrivewealthSweepState,
    DrivewealthSweepStatus,
    DrivewealthSweepStatusPartnerManagement,
    FetchState,
    InstrumentDetail,
    InstrumentStatus,
    SelectedInstrument,
} from "@/shared/types";
import Vue from "vue";
import { defineStore } from "pinia";
import _ from "lodash";
import { templatedFetchKeyStore } from "@/shared/store/fetchKeyedPlugin";
import { FetchKeyedResource } from "@/shared/fetchStateHelper";
import { useNotifierStore } from "@/shared/store/notifier";

const managedStockSymbol = "RIAMANAGED";

const emptyWorkingCopy = () => ({
    isActive: false,
    threshold: 0,
    selectedInstruments: [],
    isDirty: false,
    isInitialized: false,
    showAllAdvisoryFunds: false
});

const useSweepSubStore = defineStore("drivewealthSweep_sweeps", () => {
    const emptySavedState = () => ({ funds: [], isActive: false, threshold: 0, userId: 0, isPreenrollment: false });
    const valueSetter = (state: FetchKeyedResource<any>, dto: DrivewealthSweep) => {
        state.isActive = dto.isActive;
        state.funds = dto.funds;
        state.threshold = dto.threshold;
    };
    return templatedFetchKeyStore<DrivewealthSweep, any>(
        emptySavedState,
        valueSetter,
        drivewealthService.getSweep,
    );
});


export const initialState: DrivewealthSweepState = {
    workingCopy: emptyWorkingCopy()
};


export const useDrivewealthSweepStore = defineStore("drivewealthSweep", {
    state: (): DrivewealthSweepState => (_.cloneDeep(initialState)),
    getters: {
        sweepLoading: () => useSweepSubStore().fetchState === FetchState.Loading,
        sweepLoaded: () => useSweepSubStore().fetchState === FetchState.Loaded,
        sweepIsActive: () => useSweepSubStore().isActive,
        sweepFunds: () => useSweepSubStore().funds,
        sweepThreshold: () => useSweepSubStore().threshold,
        sweepWasActive: () => useSweepSubStore().funds.length > 0,
        isActive: state => state.workingCopy.isActive,
        isDirty: state => state.workingCopy.isDirty,
        investmentThreshold: state => state.workingCopy.threshold,
        selectedInstruments: state => state.workingCopy.selectedInstruments,
        isWorkingCopyInitialized: state => state.workingCopy.isInitialized,
        showAllAdvisoryFunds: state => state.workingCopy.showAllAdvisoryFunds,
        allocationSum: state => {
            const n = state.workingCopy.selectedInstruments
                .filter(si => si.status === InstrumentStatus.Active)
                .map(a => a.allocation)
                .filter(f => !isNaN(f))
                .reduce((total, current) => total + current, 0);

            return Math.round((n + Number.EPSILON) * 1000) / 1000;
        },
        isNormalized () {
            return Math.abs(100 - this.allocationSum) < 0.0005;
        },
        isAlreadySelected () {
            return symbol => !!this.workingCopy.selectedInstruments.find(
                i => i.stockSymbol === symbol);
        }
    },
    actions: {
        setSavedStatus (isActive: boolean) {
            useSweepSubStore().isActive = isActive;
        },
        startInitializingWorkingCopy (clearSelectedInstruments: boolean) {
            this.workingCopy.isActive = this.sweepIsActive || /* was never turned on */ this.sweepFunds.length ===
                0;
            this.workingCopy.threshold = this.sweepThreshold;
            this.workingCopy.selectedInstruments = clearSelectedInstruments ?
                [] :
                this.sweepFunds.map(f => ({
                    stockSymbol: f.symbol,
                    allocation: f.percentage,
                    status: f.status,
                    drivewealthAccountId: f.drivewealthAccountId,
                    accountType: f.accountType
                } as any));
            this.workingCopy.isDirty = false;
            this.workingCopy.isInitialized = false;
        },
        setInitialized () {
            this.workingCopy.isInitialized = true;
        },
        setDirty () {
            this.workingCopy.isDirty = true;
        },
        setIsActive (isActive: boolean) {
            this.workingCopy.isActive = isActive;
            this.workingCopy.isDirty = true;
        },
        setInvestmentThreshold (threshold: number) {
            this.workingCopy.threshold = threshold;
            this.workingCopy.isDirty = true;
        },
        setShowAllAdvisoryFunds (value: boolean) {
            this.workingCopy.showAllAdvisoryFunds = value;
        },
        addSelectedInstrument (instrument: SelectedInstrument) {
            if (this.workingCopy.selectedInstruments.find(i => i.stockSymbol === instrument.stockSymbol)) {
                return;
            }

            Vue.set(instrument, "allocation", 0);
            this.workingCopy.selectedInstruments.push(instrument);
            this.workingCopy.isDirty = true;
        },
        removeSelectedInstrument (index: number) {
            this.workingCopy.selectedInstruments.splice(index, 1);
            this.workingCopy.isDirty = true;
        },
        removeInvalidAllocations () {
            this.workingCopy.selectedInstruments =
                this.workingCopy.selectedInstruments.filter(x => x.status === InstrumentStatus.Active);

            this.workingCopy.isDirty = true;
        },
        getSweep (
            options: { username?: string, partnerId?: number, force?: boolean }
        ): Promise<any> {
            const force = options && !!options.force;
            return useSweepSubStore().fetchKeyedApiCall(options, undefined, force)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getSweepV2 (
            options: { username?: string, partnerId?: number, force?: boolean }
        ): Promise<any> {
            const force = options && !!options.force;
            return useSweepSubStore().fetchKeyedApiCall(options, undefined, force)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        postSweep (sweep: DrivewealthSweep): Promise<any> {
            const fetchKey = { ...useSweepSubStore().fetchKey };
            return drivewealthService.postSweep(sweep)
                .then(response => {
                    // the POST is not returning the state in the call,
                    // so we need to pretend like it is, use the templated mutation
                    useSweepSubStore().setValueLoaded({
                        fetchKey,
                        data: response.data
                    });
                });
        },
        postSweepV2 (sweep: DrivewealthSweep): Promise<any> {
            const fetchKey = { ...useSweepSubStore().fetchKey };
            return drivewealthService.postSweepV2(sweep)
                .then(response => {
                    // the POST is not returning the state in the call,
                    // so we need to pretend like it is, use the templated mutation
                    useSweepSubStore().setValueLoaded({
                        fetchKey,
                        data: response.data
                    });
                });
        },

        sweepAddInstrument (args: {accountId: number, stockSymbol: string}): Promise<any> {
            const fetchKey = { ...useSweepSubStore().fetchKey };
            const notifierStore = useNotifierStore();
            return drivewealthService.sweepAddInstrument(args.accountId, args.stockSymbol)
                .then(response => {
                    useSweepSubStore().setValueLoaded({
                        fetchKey,
                        data: response.data
                    });
                    notifierStore.setSuccess(`Investment added to automated funding. Please review your automated funding distribution. The distribution should equal 100%.`);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },

        postSweepStatus (sweepStatus: DrivewealthSweepStatus): Promise<any> {
            return drivewealthService.postSweepStatus(sweepStatus)
                .then(response => {
                    this.setSavedStatus(response.data.isActive);
                    return Promise.resolve();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },

        pauseSweep (options: { username?: string, partnerId?: number }): Promise<any> {
            return drivewealthService.postSweepStatus({isActive: false})
                .then(response => {
                    this.setSavedStatus(response.data.isActive);
                    this.setIsActive(response.data.isActive);
                    return Promise.resolve();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        pauseSweepEmployerManagement (options: { username?: string, partnerId?: number }): Promise<any> {
            const request: DrivewealthSweepStatusPartnerManagement = {
                isActive: false,
                cognitoUsername: options.username,
                partnerId: options.partnerId
            };
            return drivewealthService.postSweepStatusPartnerManagement(request)
                .then(response => {
                    this.setSavedStatus(response.data.isActive);
                    this.setIsActive(response.data.isActive);
                    return Promise.resolve();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        resetSweepState (
            options: { username?: string, partnerId?: number }): Promise<any> {
            return useSweepSubStore().fetchKeyedApiCall(options)
                .then(_ => {
                    this.startInitializingWorkingCopy();
                    // We need to update our linter, b/c it doesn't know about Promise.allSettled
                    // see https://stackoverflow.com/questions/60276013/
                    // property-allsettled-does-not-exist-on-type-promiseconstructor-ts2339
                    return (Promise as any).allSettled(
                        useSweepSubStore().funds.map(f => {
                            if (f.drivewealthAccountId) {
                                if (f.symbol !== managedStockSymbol) {
                                    return drivewealthService
                                        .getInstrumentByAccountId(options.username, options.partnerId,
                                        { stockSymbol: f.symbol, accountId: f.drivewealthAccountId })
                                        .then(response => {
                                            const instrument = this.workingCopy.selectedInstruments.find(
                                                i => i.stockSymbol === f.symbol);
                                            hydrateSelectedInstrument(instrument, response.data);
                                        })
                                        .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
                                }
                            } else {
                                return drivewealthService.getInstrument(options.username, options.partnerId, f.symbol)
                                .then(response => {
                                    const instrument = this.workingCopy.selectedInstruments.find(
                                        i => i.stockSymbol === f.symbol);
                                    hydrateSelectedInstrument(instrument, response.data);
                                })
                                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
                            }
                        }));
                })
                .then(_ => {
                    this.setInitialized();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        resetSweepForPreEnrollment (options: { username?: string, partnerId?: number }): Promise<any> {
            return useSweepSubStore().fetchKeyedApiCall(options)
                .then(_ => {
                    this.startInitializingWorkingCopy(true);
                })
                .then(_ => {
                    this.setInitialized();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});

function hydrateSelectedInstrument (instrument: SelectedInstrument, detail: InstrumentDetail) {
    if (!instrument || !detail) {
        return;
    }

    const vueSetter = (propertyName: keyof SelectedInstrument) => {
        Vue.set(instrument, propertyName, detail[propertyName]);
    };

    vueSetter("companyName");
    vueSetter("instrumentId");
    vueSetter("currentPrice");
    vueSetter("status");
    vueSetter("website");
    vueSetter("imageUrl");
}
